import React from 'react';
import classNames from 'classnames';

import 'app/assets/stylesheets/design_system/heading.scss';

type HeadingSizes = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type HeadingLevels = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type BaseHeadingProps = {
  fontFamily?: 'serif' | 'sans-serif';
  size?: HeadingSizes;
  as?: HeadingLevels;
};

type HeadingProps<T extends HeadingLevels = HeadingLevels> = BaseHeadingProps &
  React.ComponentPropsWithoutRef<T>;

const sizeToHeadingTag: Record<HeadingSizes, HeadingLevels> = {
  xs: 'h5',
  sm: 'h4',
  md: 'h3',
  lg: 'h2',
  xl: 'h1',
};

/**
 * Heading component used for section headers, article titles, paper titles, etc.
 * Available in serif and sans-serif font types and various sizes.
 * HeadingLink sibling component is available when you need a heading to act as a link (see `../link/HeadingLink.tsx`).
 *
 * Props:
 * - `fontFamily`: Font type for the heading (default: 'sans-serif')
 *               Choose between 'serif' | 'sans-serif'
 * - `size`: Controls the font-size and line-height of the heading (default: 'lg')
 *           Choose between 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * - `as`: HTML heading level to render as. Without specifying, the component's heading level
 *         is determined by the size (e.g. 'md' will render as <h3>)
 *         Choose between 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Heading } from 'js/design_system/Heading';
 *
 * <Heading>A Basic Heading</Heading>
 * ```
 * ---
 *
 * ## Custom Font Type and Size
 *
 * @example
 *
 * ```tsx
 * import { Heading } from 'js/design_system/Heading';
 *
 * <Heading fontFamily='serif' size='lg'>Large Serif Heading</Heading>
 * ```
 * ---
 *
 * ## Custom Heading Level
 * ```tsx
 * import { Heading } from 'js/design_system/Heading';
 *
 * <Heading as='h3' size="sm">Custom Heading Level</Heading>
 * ```
 * ---
 *
 */
export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      fontFamily = 'sans-serif',
      size = 'lg',
      as,
      className,
      children,
      ...restProps
    },
    ref,
  ) => {
    const HeadingTag = as || sizeToHeadingTag[size];

    const classes = classNames(
      className,
      `ds2-5-heading-${fontFamily}-${size}`,
    );

    return (
      <HeadingTag className={classes} {...restProps} ref={ref}>
        {children}
      </HeadingTag>
    );
  },
);
